/**
 * Pure derivations for the AsEmployeeCheckpointInspectionCard MetaRow and
 * helper row (ER-11336 Steps 3–4). Kept outside the SFC so the badge-row logic
 * and the Figma helper copy can be unit-tested without mounting the card, and
 * so the copy lives in exactly one place for the post-Figma copy sweep.
 */

import { CARD_STATUSES } from "./asEmployeeCheckpointInspectionCardConstants";

/** InspectionQuizStatusBadge variant per normalised card status. */
export const QUIZ_BADGE_STATUS_BY_CARD_STATUS = {
  not_started: "Not Attempted",
  quiz_not_attempted: "Not Attempted",
  quiz_failed: "Failed",
  inspection_ready: "Passed",
  inspection_failed: "Passed",
  passed: "Passed",
  reset: "Not Attempted",
  not_applicable: "Not Required",
};

/** InspectionInspectedStatusBadge state per normalised card status. */
export const INSPECTED_BADGE_STATUS_BY_CARD_STATUS = {
  not_started: "inspection-pending",
  quiz_not_attempted: "inspection-pending",
  quiz_failed: "inspection-pending",
  inspection_ready: "inspection-pending",
  inspection_failed: "failed",
  passed: "passed",
  reset: "inspection-pending",
  not_applicable: "not-required",
};

/**
 * True when the employee has finished the medium (video watched / article read).
 * Mirrors the derivation documented on InspectionArticleReadStatusBadge: every
 * state except not_started and reset implies the medium was consumed (the
 * builder only reaches quiz/inspection states once the medium is complete).
 */
export function isLearningSeen(status) {
  return !["not_started", "reset"].includes(status);
}

/**
 * Helper-text sentence per shape (media / hasQuiz) and normalised status.
 * ER-11336 Step 4 — copy is keyed off shape + status, never accepted as a prop.
 *
 * Strings marked `// Figma` were quoted in the ER-11336 spec / Eric Kish's
 * comments. The remaining sentences are best-effort canonical copy that still
 * needs a one-file sweep against the Figma helper nodes once the exact copy is
 * supplied — they all live here so the sweep touches a single function.
 */
export function buildHelperCopy({ status, media, hasQuiz }) {
  if (!CARD_STATUSES.includes(status)) return "";

  if (status === "not_applicable") {
    return "The coach decided that this checkpoint is not applicable to you"; // Figma
  }

  // Inspector-only checkpoints (no medium, no quiz) explain themselves via the
  // inspection badge; the passed variant deliberately carries no helper row.
  if (media === "none") {
    if (status === "passed") return ""; // Figma — the only card with no helper text
    return "A coach can pass you on this checkpoint at their discretion. Ask a certified coach to inspect you"; // Figma
  }

  const mediumVerb = media === "video" ? "watching the video" : "reading the article";

  switch (status) {
    case "not_started":
      if (hasQuiz) return `Start by ${mediumVerb} and then take the quiz`; // Figma prefix
      return `Start by ${mediumVerb} and then ask a coach to inspect you`;
    case "quiz_not_attempted":
      return "Take the quiz to complete this checkpoint";
    case "quiz_failed":
      return "Retake the quiz to pass this checkpoint";
    case "inspection_ready":
      return hasQuiz
        ? "You have passed the quiz. Ask a coach to inspect you when you're ready"
        : "Ask a coach to inspect you when you're ready";
    case "inspection_failed":
      return "Your inspection did not pass. Review the feedback and ask for another inspection";
    case "passed":
      return "You have completed this checkpoint";
    case "reset":
      return hasQuiz
        ? `Start by ${mediumVerb} and then take the quiz again`
        : `Start by ${mediumVerb} and then ask a coach to inspect you again`;
    default:
      return "";
  }
}

/**
 * ER-11336 Step 2 / acceptance criterion 1: an impossible shape + status
 * combination must warn in dev instead of rendering silently (the MetaRow would
 * otherwise degrade to "Not Required" badges with no explanation). The shape is
 * described by the facets the checkpoint actually has:
 *   - hasQuiz: the checkpoint carries a quiz step (quiz_* statuses are only
 *     valid when true)
 *   - hasInspection / isInspectorOnly: the checkpoint ends in an inspection.
 *     isInspectorOnly is the media=none, no-quiz, inspection variant whose only
 *     valid statuses are inspection_* / passed (passes carry no warning).
 * A notApplicable card is universal: its overridden facets are meaningless, so
 * no status combination can warn.
 *
 * Returns an array of human-readable warnings (empty for valid combinations).
 * Pure and unit-testable; the card invokes it from a dev-only watcher.
 */
export function invalidShapeStatusWarnings({
  status,
  hasQuiz = false,
  hasInspection = false,
  isInspectorOnly = false,
  notApplicable = false,
}) {
  if (!CARD_STATUSES.includes(status) || notApplicable || status === "not_applicable") {
    return [];
  }

  const warnings = [];

  if (!hasQuiz && (status === "quiz_not_attempted" || status === "quiz_failed")) {
    warnings.push(
      `status "${status}" was supplied for a checkpoint without a quiz step (hasQuiz=false). ` +
        'quiz_* statuses are only valid when hasQuiz=true, so the quiz badge renders "Not Required". ' +
        "Check the caller's facets."
    );
  }

  if (
    !isInspectorOnly &&
    !hasInspection &&
    (status === "inspection_ready" || status === "inspection_failed")
  ) {
    warnings.push(
      `status "${status}" was supplied for a checkpoint without an inspection step (hasInspection=false). ` +
        "inspection_* statuses are only valid when the checkpoint ends in an inspection, so the " +
        'inspected badge renders "Not Required". Check the caller\'s facets.'
    );
  }

  return warnings;
}
